import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoogleLoginButton from '../components/GoogleLoginButton';

/**
 * Login page -- route: /login
 *
 * Supports a ?redirect=<path> query param. After successful sign-in the user
 * is navigated back to that path. Already-logged-in visitors are redirected
 * immediately so they never see the login form.
 */
const Login = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse the redirect destination from ?redirect=<path>
  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/';

  // If the user is already logged in (or becomes logged in after Google OAuth
  // callback fires in AuthContext), redirect them to the intended destination.
  useEffect(() => {
    if (user) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, redirectTo, navigate]);

  // While user is set, show nothing (redirect effect runs immediately after).
  // This prevents a flash of the login form for an already-authenticated user.
  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="card p-8 text-center space-y-6">
          {/* Lock icon */}
          <div className="w-16 h-16 mx-auto rounded-full bg-battle-dark border-2 border-army flex items-center justify-center">
            <svg className="w-8 h-8 text-brand-yellow" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {/* Headline */}
          <div>
            <h1
              className="text-3xl font-black uppercase tracking-tight text-white mb-2"
              data-testid="login-headline"
            >
              Sign in to watch
            </h1>
            <p
              className="text-gray-400 text-sm leading-relaxed"
              data-testid="login-subline"
            >
              Exclusive battles require a free account.
              Sign in with Google -- it only takes a second.
            </p>
          </div>

          {/* Google sign-in button */}
          <div className="flex justify-center">
            <GoogleLoginButton id="googleSignInButtonLogin" />
          </div>

          {/* Branding note */}
          <p className="text-gray-600 text-xs">
            Google sign-in only &middot; No password needed
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
